"use client"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { ReactNode } from "react"
import { getNonUmbracoUrl } from "utils"

interface AnchorLinkProps {
  href: string
  children: ReactNode
  className?: string
  onClick?: () => void
  preserveQueryParams?: boolean
  style?: React.CSSProperties
}

/**
 * A component that renders a link to an anchor on the current page or another page
 *
 * Usage:
 * <AnchorLink href="#my-section">Go to Section</AnchorLink>
 * <AnchorLink href="/about#team">Go to Team Section on About Page</AnchorLink>
 */
const AnchorLink = ({
  href,
  children,
  className,
  onClick,
  style,
}: AnchorLinkProps) => {
  const pathname = usePathname()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // If the link is to an anchor on the current page
    if (
      href.startsWith("#") ||
      (href.includes("#") && href.split("#")[0] === pathname)
    ) {
      e.preventDefault()

      // Get the anchor ID
      const anchorId = href.includes("#") ? href.split("#")[1] : ""

      if (anchorId) {
        // Find the element with the ID
        const element = document.getElementById(anchorId)
        if (element) {
          // Scroll to the element
          element.scrollIntoView({ behavior: "smooth", block: "end" })

          // Update URL without page reload
          const newUrl = `${pathname}#${anchorId}`
          window.history.pushState({}, "", newUrl)
        }
      }

      // Call the onClick handler if provided
      if (onClick) {
        onClick()
      }
    } else if (onClick) {
      onClick()
    }
  }

  return (
    <Link
      href={getNonUmbracoUrl(href)}
      className={className}
      onClick={handleClick}
      style={style}
    >
      {children}
    </Link>
  )
}

export default AnchorLink
